import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/slices/authSlice";
import Header from "./Header";
import Footer from "./Footer";
import { FaSignOutAlt } from "react-icons/fa";

const adminLinks = [
  { to: "/admin/dashboard", icon: "📊", label: "Dashboard" },
  { to: "/admin/users", icon: "👥", label: "Users" },
  { to: "/admin/vendors", icon: "🏪", label: "Vendors" },
  { to: "/admin/machines", icon: "🏗️", label: "Machines" },
  { to: "/admin/categories", icon: "📂", label: "Categories" },
  { to: "/admin/bookings", icon: "📅", label: "Bookings" },
  { to: "/admin/subcategories/add", icon: "➕", label: "Add Category" },
];

const vendorLinks = [
  { to: "/vendor/dashboard", icon: "📊", label: "Dashboard" },
  { to: "/vendor/machines", icon: "🏗️", label: "My Machines" },
  { to: "/vendor/machines/add", icon: "➕", label: "Add Machine" },
  { to: "/vendor/orders", icon: "📋", label: "Orders" },
  { to: "/profile", icon: "👤", label: "Profile" },
];

const customerLinks = [
  { to: "/bookings", icon: "📅", label: "My Bookings" },
  { to: "/machines", icon: "🏗️", label: "Browse Machines" },
  { to: "/profile", icon: "👤", label: "Profile" },
];

const DashboardLayout = () => {
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "dark");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", "dark");
  }, [theme]);

  const toggleTheme = () =>
    setTheme((prev) => (prev === "light" ? "dark" : "light"));

  const getLinks = () => {
    if (user?.role === "admin") return adminLinks;
    if (user?.role === "vendor") return vendorLinks;
    return customerLinks;
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const links = getLinks();

  return (
    <div style={{ backgroundColor: "var(--bg-secondary)", minHeight: "100vh" }}>
      <Header theme={theme} toggleTheme={toggleTheme} />
      <div style={{ display: "flex" }}>
        {/* Sidebar */}
        <aside
          style={{
            width: sidebarOpen ? "250px" : "60px",
            background: "var(--sidebar-bg)",
            minHeight: "calc(100vh - 64px)",
            flexShrink: 0,
            transition: "width 0.3s ease",
            borderRight: "1px solid #222",
            display: "flex",
            flexDirection: "column",
            position: "sticky",
            top: "64px",
            height: "calc(100vh - 64px)",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {/* Sidebar Header */}
          <div
            style={{
              padding: "16px 12px",
              borderBottom: "1px solid #222",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {sidebarOpen && (
              <div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#888",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                  }}
                >
                  Logged in as
                </div>
                <div
                  style={{
                    color: "#fbbf24",
                    fontWeight: 700,
                    fontSize: "14px",
                    marginTop: "2px",
                  }}
                >
                  {user?.name}
                </div>
                <div
                  style={{
                    color: "#FF7618",
                    fontSize: "12px",
                    textTransform: "capitalize",
                  }}
                >
                  {user?.role}
                </div>
              </div>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                background: "transparent",
                border: "none",
                color: "#fbbf24",
                cursor: "pointer",
                fontSize: "18px",
                padding: "4px",
              }}
            >
              {sidebarOpen ? "◀" : "▶"}
            </button>
          </div>

          {/* Nav Links */}
          <nav style={{ padding: "12px 0", flex: 1 }}>
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to.endsWith("dashboard")}
                style={({ isActive }) => ({
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: sidebarOpen ? "12px 20px" : "12px 16px",
                  color: isActive ? "#FF7618" : "#fbbf24",
                  textDecoration: "none",
                  fontWeight: 500,
                  fontSize: "14px",
                  borderLeft: `3px solid ${isActive ? "#FF7618" : "transparent"}`,
                  background: isActive ? "#1f1f1f" : "transparent",
                  transition: "all 0.2s",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                })}
                onMouseEnter={(e) => {
                  if (!e.currentTarget.style.background.includes("1f")) {
                    e.currentTarget.style.background = "#1a1a1a";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.currentTarget.classList.contains("active")) {
                    e.currentTarget.style.background = "transparent";
                  }
                }}
              >
                <span style={{ fontSize: "18px", flexShrink: 0 }}>
                  {link.icon}
                </span>
                {sidebarOpen && <span>{link.label}</span>}
              </NavLink>
            ))}
          </nav>

          {/* Logout */}
          <div style={{ padding: "12px", borderTop: "1px solid #222" }}>
            <button
              onClick={handleLogout}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: sidebarOpen ? "10px 16px" : "10px",
                color: "#ef4444",
                background: "transparent",
                border: "1px solid #ef4444",
                borderRadius: "8px",
                cursor: "pointer",
                width: "100%",
                fontSize: "13px",
                fontWeight: 600,
              }}
            >
              <span>
                {" "}
                <FaSignOutAlt />
              </span>
              {sidebarOpen && <span>Sign Out</span>}
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main style={{ flex: 1, padding: "24px", minWidth: 0 }}>
          <Outlet context={{ theme }} />
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default DashboardLayout;
